/**
 * Utilitaires pour lancer les applications Microsoft desktop et gérer les liens de retour.
 *
 * Policy applicative (partout) :
 * - Ne jamais auto-lancer un protocole ms-* (ms-outlook:, ms-word:, …) sans geste utilisateur.
 * - Le handoff / CTA ouvre Famla web sur /auth/login (puis /map/new après connexion).
 * - openHostDesktopApp reste le seul chemin pour ouvrir le desktop (clic explicite).
 */
import type { OfficeHostType } from './useOfficeContext';

export type OfficePlatformType =
    | 'OfficeOnline'
    | 'PC'
    | 'Mac'
    | 'iOS'
    | 'Android'
    | 'Universal'
    | 'Unknown';

const DESKTOP_PROTOCOLS: Record<OfficeHostType, string | null> = {
    Outlook: 'ms-outlook:',
    Word: 'ms-word:',
    Excel: 'ms-excel:',
    Unknown: null,
};

export function isLinuxPlatform(): boolean {
    const platform = navigator.platform?.toLowerCase() ?? '';
    const userAgent = navigator.userAgent?.toLowerCase() ?? '';
    return platform.includes('linux') || userAgent.includes('linux');
}

export function getDesktopProtocolUris(hostApp: OfficeHostType): string[] {
    const primary = DESKTOP_PROTOCOLS[hostApp];
    if (!primary) {
        return [];
    }

    // Un seul protocole : évite deux dialogues OS (msoutlook + ms-outlook).
    return [primary];
}

export function getDesktopProtocolUri(hostApp: OfficeHostType): string | null {
    const uris = getDesktopProtocolUris(hostApp);
    return uris[0] ?? null;
}

/**
 * Auto-launch desktop is disabled everywhere (Firefox/OS would force local Outlook).
 * Desktop is only opened via explicit user choice (openHostDesktopApp).
 */
export function shouldLaunchDesktop(_hostPlatform: OfficePlatformType): boolean {
    return false;
}

export function buildOutlookWebInboxUrl(): string {
    return 'https://outlook.office.com/mail/';
}

export function buildOutlookWebReturnUrl(itemId?: string): string {
    if (!itemId) {
        return buildOutlookWebInboxUrl();
    }
    return `https://outlook.office.com/mail/deeplink/compose?itemid=${encodeURIComponent(itemId)}`;
}

export function buildHostWebReturnUrl(
    hostApp: OfficeHostType,
    outlookItemId?: string
): string | null {
    switch (hostApp) {
        case 'Outlook':
            return buildOutlookWebReturnUrl(outlookItemId);
        case 'Word':
            return 'https://www.office.com/launch/word';
        case 'Excel':
            return 'https://www.office.com/launch/excel';
        default:
            return null;
    }
}

export function canUseDesktopProtocol(): boolean {
    const platform = navigator.platform?.toLowerCase() ?? '';
    const userAgent = navigator.userAgent?.toLowerCase() ?? '';
    return platform.includes('win') || platform.includes('mac') || userAgent.includes('mac');
}

export interface DesktopReturnUrls {
    protocolUri: string | null;
    webUrl: string | null;
    /** Safe default URL for consumers — always prefers web over ms-* protocol. */
    desktopReturnUrl: string | null;
}

export function buildDesktopReturnUrls(
    hostApp: OfficeHostType,
    outlookItemId?: string
): DesktopReturnUrls {
    const protocolUri = getDesktopProtocolUri(hostApp);
    const webUrl = buildHostWebReturnUrl(hostApp, outlookItemId);
    // Prefer web so any single-URL consumer never auto-triggers ms-* handlers
    const desktopReturnUrl = webUrl || protocolUri;

    return { protocolUri, webUrl, desktopReturnUrl };
}

/**
 * @deprecated Auto-launch disabled. Prefer openFamlaWebUrl + explicit Web/Desktop choice on landing.
 * Kept as a no-op so legacy call sites cannot force Outlook/Word/Excel desktop.
 */
export function launchMicrosoftDesktop(
    _hostApp: OfficeHostType,
    _hostPlatform: OfficePlatformType,
    _onBlocked?: () => void
): boolean {
    return false;
}

/**
 * Ouvre une URL Famla web via Office.context.ui.openBrowserWindow si disponible.
 */
export function openFamlaWebUrl(url: string): void {
    if (typeof Office !== 'undefined' && Office.context?.ui?.openBrowserWindow) {
        Office.context.ui.openBrowserWindow(url);
        return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Ouvre l'application hôte dans le navigateur (Outlook/Word/Excel Web).
 */
export function openHostWebApp(webUrl?: string | null): void {
    if (!webUrl) return;
    window.open(webUrl, '_blank', 'noopener,noreferrer');
}

function launchProtocolUri(protocolUri: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = protocolUri;
    document.body.appendChild(iframe);

    window.setTimeout(() => {
        document.body.removeChild(iframe);
    }, 2000);

    const link = document.createElement('a');
    link.href = protocolUri;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Ouvre l'application desktop via le protocole ms-* — uniquement sur geste utilisateur explicite
 * (ex. bouton « Open desktop app » sur la page de handoff).
 */
export function openHostDesktopApp(
    protocolUri?: string | null,
    onUnsupportedPlatform?: () => void,
    hostApp?: OfficeHostType
): boolean {
    const uris = hostApp
        ? getDesktopProtocolUris(hostApp)
        : protocolUri
            ? [protocolUri]
            : [];

    if (uris.length === 0) {
        return false;
    }

    if (!canUseDesktopProtocol() && !isLinuxPlatform()) {
        onUnsupportedPlatform?.();
        return false;
    }

    if (!canUseDesktopProtocol() && isLinuxPlatform()) {
        onUnsupportedPlatform?.();
    }

    try {
        launchProtocolUri(uris[0]);
        return true;
    } catch {
        return false;
    }
}

/**
 * @deprecated Préférer openHostWebApp / openHostDesktopApp pour proposer les deux choix.
 * Never auto-navigates to ms-* protocols — opens web when available.
 */
export function openDesktopReturn(
    desktopReturnUrl?: string | null,
    webFallbackUrl?: string | null
): void {
    const webUrl =
        webFallbackUrl ||
        (desktopReturnUrl && !desktopReturnUrl.startsWith('ms-') ? desktopReturnUrl : null);

    if (webUrl) {
        window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
}

export function getHostAppLabel(hostApp: OfficeHostType): string {
    switch (hostApp) {
        case 'Outlook':
            return 'Outlook';
        case 'Word':
            return 'Word';
        case 'Excel':
            return 'Excel';
        default:
            return 'Office';
    }
}
